import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Copy, Loader2, Phone, MoreVertical } from "lucide-react";

export function MotorizadosTab({ deliverers, onRefresh, onSelect }) {
  const { t } = useTranslation();
  const { authAxios } = useAuth();
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [vehicle, setVehicle] = useState("moto");
  const [loading, setLoading] = useState(false);

  const filtered = deliverers.filter(d => !search || d.name.toLowerCase().includes(search.toLowerCase()));

  const create = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAxios.post("/deliverers", { name, phone, vehicle });
      toast.success(t("team.riderAdded"));
      setShowForm(false);
      setName("");
      setPhone("");
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("team.riderError")); }
    setLoading(false);
  };

  const toggleActive = async (d) => {
    try {
      await authAxios.patch(`/deliverers/${d.id}/toggle-active`);
      toast.success(d.active ? t("team.deactivated") : t("team.activated"));
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || t("team.error")); }
  };

  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success(t("team.tokenCopied"));
  };

  return (
    <div data-testid="motorizados-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase" onClick={() => setShowForm(true)} data-testid="add-motoboy-btn">
          {t("team.addRider")}
        </Button>
        <div className="ml-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t("team.searchByName")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 w-64 bg-white border-gray-200"
            data-testid="search-motoboys"
          />
        </div>
      </div>

      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead>
            <tr className="border-b text-gray-500 text-xs uppercase">
              <th className="text-left p-3 font-medium">{t("team.colName")}</th>
              <th className="text-left p-3 font-medium">{t("team.colPhone")}</th>
              <th className="text-center p-3 font-medium">{t("team.colActive")}</th>
              <th className="text-left p-3 font-medium">{t("team.colToken")}</th>
              <th className="p-3 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map(d => (
              <tr key={d.id} className="hover:bg-gray-50 cursor-pointer" data-testid={`motoboy-row-${d.id.slice(-6)}`}>
                <td className="p-3">
                  <span className="text-emerald-700 font-medium hover:underline" onClick={() => onSelect(d)}>{d.name}</span>
                </td>
                <td className="p-3 text-gray-600">
                  <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-gray-400" />{d.phone}</span>
                </td>
                <td className="p-3 text-center">
                  <Switch checked={d.active !== false} onCheckedChange={() => toggleActive(d)} data-testid={`toggle-active-${d.id.slice(-6)}`} />
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono truncate max-w-[180px]">{d.token || "-"}</code>
                    {d.token && (
                      <button onClick={() => copyToken(d.token)} className="text-gray-400 hover:text-gray-600" data-testid={`copy-token-${d.id.slice(-6)}`}>
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                <td className="p-3">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onSelect(d)}>
                    <MoreVertical className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-gray-400">{t("team.noRiders")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="uppercase font-bold">{t("team.addRiderTitle")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3 mt-2">
            <div>
              <Label>{t("team.colName")} *</Label>
              <Input value={name} onChange={e => setName(e.target.value)} required className="mt-1" data-testid="motoboy-name" />
            </div>
            <div>
              <Label>{t("team.colPhone")} *</Label>
              <Input value={phone} onChange={e => setPhone(e.target.value)} required placeholder="+34..." className="mt-1" />
            </div>
            <div>
              <Label>{t("team.vehicle")}</Label>
              <Select value={vehicle} onValueChange={setVehicle}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="moto">{t("team.vehMoto")}</SelectItem>
                  <SelectItem value="bicycle">{t("team.vehBicycle")}</SelectItem>
                  <SelectItem value="car">{t("team.vehCar")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t("team.cancel")}</Button>
              <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-motoboy-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("team.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
